/**
 * This functional component "Add Media" provides form to add new Media or update existing Media .
 * Created Date:23-April-2024
 */
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { TextStyle } from "@tiptap/extension-text-style";
import { EditorContent, Extension, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import FontTypeEnum from "../../../../constants/enums/font-type-enum";
import {
  freeTextDirectionOptions,
  freeTextHeightLevelsOptions,
  freeTextSpeedLevelsOptions,
} from "../../../../constants/options.constant";
import { useStore } from "../../../../contexts/store-provider";
import { IFolderOption } from "../../../../models/ICommon";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import FormikFormCheckBox from "../../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddFreeTextMediaFileValidateSchema,
  AddMediaHeightWidthalidateSchema,
  AddMediaPriceValidateSchema,
  AddMediaValidateSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../../shared-components/button/loader-button";
import Label from "../../../../shared-components/label/label";
import { HexColorPicker, HexColorInput } from "react-colorful";
import "./add-edit-free-text-form.css";
import { formatMessage } from "../../../../translations/format-message";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  selectedFolderType: number;
  allFolders: IFolderOption[];
  onModalClosed: () => void;
  initialValues: any;
  isLoading: boolean;
}

const AddFreeTextMediaForm = (props: IProps) => {
  const { preferencesStore } = useStore();
  const { getMediaAccess } = preferencesStore;
  const { initialValues, submitHandler, isLoading, onModalClosed } = props;

  const [data, setData] = useState({ ...initialValues });
  const [currentStep, setCurrentStep] = useState<number>(0);

  useEffect(() => {
    setData({ ...initialValues });
  }, [initialValues]);

  const handleNextStep = (newData: any, final: boolean = false) => {
    setData((prev: any) => ({ ...prev, ...newData }));
    if (final) {
      let tempValues: any = { ...newData };
      submitHandler(tempValues);
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = (newData: any) => {
    setData((prev: any) => ({ ...prev, ...newData }));
    setCurrentStep((prev) => prev - 1);
  };

  const getFolderCategoryById = (): any => {
    const item = props.allFolders.find(
      (item: any) => item.id === getMediaAccess.folderId,
    );
    return item ? item.category : false;
  };

  const StepOne = (props1: any) => {
    const handleSubmit = (values: any) => {
      props1.next({ ...values });
    };
    return (
      <Formik
        initialValues={props1.data}
        onSubmit={handleSubmit}
        validationSchema={AddFreeTextMediaFileValidateSchema}
        validateOnBlur={false}
        enableReinitialize
      >
        {({ values, isValid }) => {
          return (
            <Form id="formBody">
              <div className="modal-body">
                <div className="pt-3">
                  <div className="progress step-progress">
                    <div className="progress-bar" role="progressbar"></div>
                  </div>
                  <div className="step-container d-flex justify-content-between">
                    <div className="step-circle active">
                      1{" "}
                      <span className="step-detail">
                        <FormattedMessage id="media_details" />
                      </span>
                    </div>
                    <div className="step-circle">
                      2{" "}
                      <span className="step-detail">
                        <FormattedMessage id="free_text_config" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 col-12">
                    <div className="form-group formSet">
                      <Label
                        className="label-style"
                        required={true}
                        label="free_text_name"
                      />
                      <Field
                        data-testid="imageName"
                        className="form-control"
                        name="MediaName"
                        placeholder="free_text_name"
                        type="text"
                        disabled={props.id > 0 ? true : false}
                        validcharacter={["_"]}
                        invalidcharacter={[" "]}
                        as={FormikFormInput}
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group formSet">
                      <Label
                        className="label-style"
                        required={false}
                        label="comment"
                      />
                      <Field
                        rows={props.id == -1 ? 3 : 2}
                        id="Comment"
                        className="form-control"
                        name="Comment"
                        placeholder="comment"
                        type="text"
                        as={FormikFormTextArea}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-3 d-flex justify-content-center">
                  <LoaderButton
                    type={"submit"}
                    id="SaveTenant"
                    isLoading={props.isLoading}
                    text="next"
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  };

  const StepTwo = (props2: any) => {
    const [freeTextHTML, setFreeTextHTML] = useState<string>(
      props.initialValues?.FreeTextHtml || "",
    );

    const [freeText, setFreeText] = useState<string>(
      props.initialValues?.FreeText || "",
    );

    const [editorText, setEditorText] = useState<string>(
      props.initialValues?.FreeText || "",
    );

    const formikRef = useRef<any>(null);
    const [freeTextError, setFreeTextError] = useState("");
    const fontSizes = Array.from({ length: 51 }, (_, i) => i + 10);

    const FontSize = Extension.create({
      name: "fontSize",
      addGlobalAttributes() {
        return [
          {
            types: ["textStyle"],
            attributes: {
              fontSize: {
                default: null,
                parseHTML: (element) => element.style.fontSize,
                renderHTML: (attributes) => {
                  if (!attributes.fontSize) return {};
                  return { style: `font-size: ${attributes.fontSize}` };
                },
              },
            },
          },
        ];
      },
      addCommands() {
        return {
          setFontSize:
            (fontSize) =>
            ({ chain }) => {
              return chain().setMark("textStyle", { fontSize }).run();
            },
        };
      },
    });

const getFontType = (font: number): string => {
  switch (font) {
    case 0:
      return "Arial";
    case 1:
      return "Meiryo";
    case 2:
      return "MS Mincho";
    default:
      return "Arial";
  }
};

    const [fontFamily, setFontFamily] = useState<string>(
      (getFontType(props.initialValues?.FontType) as string) || "Arial",
    );
    const [fontSize, setFontSize] = useState(
      props.initialValues?.FontSize > 0
        ? props.initialValues.FontSize + "px"
        : "20px",
    );
    const [fontColor, setFontColor] = useState(
      props.initialValues?.Color || "#ffffff",
    );
    const colorPickerRef = useRef<HTMLDivElement>(null);

    // Refs so onCreate and updateContent always read the latest values
    const fontColorRef = useRef(fontColor);
    const fontFamilyRef = useRef(fontFamily);
    const fontSizeRef = useRef(fontSize);
    fontColorRef.current = fontColor;
    fontFamilyRef.current = fontFamily;
    fontSizeRef.current = fontSize;

    /**
     * Uses ProseMirror's setStoredMarks API to queue marks for the next
     * typed character. Unlike TipTap's setColor/setFontFamily which operate
     * on selections, storedMarks work even when the document is completely
     * empty or the selection is a cursor with no text selected.
     */
    const applyStoredMarks = (editorInstance: any) => {
      if (!editorInstance) return;

      const { state, view } = editorInstance;
      const { schema } = state;

      const textStyleMark = schema.marks.textStyle;

      if (!textStyleMark) return;

      const marks = [
        textStyleMark.create({
          color: fontColorRef.current,
          fontFamily: fontFamilyRef.current,
          fontSize: fontSizeRef.current,
        }),
      ];

      view.dispatch(state.tr.setStoredMarks(marks));

      // Force editor DOM styles too
      const dom = view.dom as HTMLElement;

      dom.style.color = fontColorRef.current;
      dom.style.fontFamily = fontFamilyRef.current;
      dom.style.fontSize = fontSizeRef.current;
    };

    const ensureEditorStylePersistence = (editorInstance: any) => {
      if (!editorInstance) return;

      const text = editorInstance.getText().trim();

      if (text.length === 0 && editorInstance.getHTML() !== "<p></p>") {
        editorInstance.commands.clearContent();
      }

      applyStoredMarks(editorInstance);
    };

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ hardBreak: false }),
        TextStyle,
        FontFamily,
        Color,
        FontSize,
      ],
      content: props.initialValues?.FreeTextHtml,
      autofocus: true,
      onCreate({ editor: editorInstance }) {
          if (editorInstance) {
          editorInstance.commands.focus("end");
          }
        ensureEditorStylePersistence(editorInstance);        
      },
      editorProps: {
        attributes: {
          style: `
      font-family: ${fontFamily};
      font-size: ${fontSize};
      color: ${fontColor};
      min-height: 120px;
      padding: 8px;
      border: 1px solid #ccc;
      caret-color: ${fontColor};
    `,
        },
        handleTextInput(view, from, to, text) {
          if (view.composing) return false;
          ensureEditorStylePersistence(editor);
          return false;
        },
        handleDOMEvents: {
          focus: (_view) => {
            ensureEditorStylePersistence(editor);
            return false;
          },
          compositionend: (_view) => {
            requestAnimationFrame(() => {
              ensureEditorStylePersistence(editor);

              if (editor) {
                const text = editor.getText().replace(/\n/g, "");
                const html = editor.getHTML();
                setFreeText(text);
                setFreeTextHTML(html);
                setEditorText(text);

                if (text.trim().length === 0) {
                  setFreeTextError("free_text_content_required");
                } else {
                  setFreeTextError("");
                }

                // if (formikRef.current) {
                //   // NEW — validate after value is set
                //   formikRef.current.setFieldTouched("FreeText", true, false);
                //   formikRef.current.setFieldValue("FreeText", text, true);
                // }
              }
            });
            return false;
          },
        },
        handleKeyDown(view, event) {
          // Block Enter key
          if (view.composing) return false;
          if (event.key === "Enter") {
            event.preventDefault();
            return true;
          }

          // Detect delete-all scenarios
          const isDeleteKey =
            event.key === "Backspace" || event.key === "Delete";

          const isSelectAll =
            (event.ctrlKey || event.metaKey || event.altKey) &&
            event.key.toLowerCase() === "a";

          // After delete/select-all operations,
          // restore marks immediately on next frame
          if (isDeleteKey || isSelectAll) {
            requestAnimationFrame(() => {
              ensureEditorStylePersistence(editor);
            });
          }

          return false;
        },

        // handleDOMEvents: {
        //   focus: (_view) => {
        //     ensureEditorStylePersistence(editor);
        //     return false;
        //   },
        // },

        handlePaste(view, event) {
          event.preventDefault();
          const plainText = event.clipboardData?.getData("text/plain") ?? "";
          const sanitized = plainText.replace(/[\n\r\t]/g, "");
          if (sanitized.length > 0) {
            // Insert as styled HTML so pasted text matches current editor styles
            editor?.commands.insertContent(
              `<span style="color: ${fontColorRef.current}; font-family: ${fontFamilyRef.current}; font-size: ${fontSizeRef.current}">${sanitized}</span>`,
            );
          }
          return true;
        },
      },
    });

    useEffect(() => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .selectAll()
        .setFontFamily(fontFamily)
        .setColor(fontColor)
        .setTextSelection(editor.state.doc.content.size)
        // .blur()
        .run();
      // Keep storedMarks in sync so cursor typing uses latest styles
      applyStoredMarks(editor);
    }, [fontFamily, fontColor, editor]);

    useEffect(() => {
      if (!editor) return;

      // Apply size to existing content
      editor
        .chain()
        .focus()
        .selectAll()
        .setMark("textStyle", { fontSize })
        .setTextSelection(editor.state.doc.content.size)
        .run();

      // IMPORTANT:
      // Re-apply stored marks so next typed character
      // keeps current color/family/size even in empty editor
      ensureEditorStylePersistence(editor);
    }, [fontSize, editor]);

    useEffect(() => {
      if (!editor) return;
      // if (editor.view.composing) return;

      const updateContent = () => {
        const isComposing = editor.view.composing;
        const text = editor.getText().replace(/\n/g, "");
        const html = editor.getHTML();

        // When editor becomes empty, preserve styling
        if (!isComposing) {
          if (text.length === 0) {
            ensureEditorStylePersistence(editor);
          }

          // Check for multiple colors
          const colorMatches = html.match(/color:\s*([^;'"]+)/g);

          if (colorMatches) {
            const uniqueColors = new Set(
              colorMatches.map((c) => c.replace(/color:\s*/, "").trim()),
            );

            if (uniqueColors.size > 1) {
              editor.chain().focus().selectAll().setColor(fontColor).run();

              return;
            }
          }

          // Check for multiple font families
          const fontFamilyMatches = html.match(/font-family:\s*([^;'"]+)/g);

          if (fontFamilyMatches) {
            const uniqueFontFamilies = new Set(
              fontFamilyMatches.map((f) =>
                f.replace(/font-family:\s*/, "").trim(),
              ),
            );

            if (uniqueFontFamilies.size > 1) {
              editor
                .chain()
                .focus()
                .selectAll()
                .setFontFamily(fontFamily)
                .run();

              return;
            }
          }

          // Check for multiple font sizes
          const fontSizeMatches = html.match(/font-size:\s*([^;'"]+)/g);

          if (fontSizeMatches) {
            const uniqueFontSizes = new Set(
              fontSizeMatches.map((f) => f.replace(/font-size:\s*/, "").trim()),
            );

            if (uniqueFontSizes.size > 1) {
              editor
                .chain()
                .focus()
                .selectAll()
                .setMark("textStyle", { fontSize })
                .run();

              return;
            }
          }
        }

        setFreeText(text);
        setFreeTextHTML(html);
        setEditorText(text);

        const trimmedText = text.trim();

        if (trimmedText.length === 0) {
          setFreeTextError("free_text_content_required");
        } else if (text.length > 100) {
          setFreeTextError("free_text_content_max_length");
        } else {
          setFreeTextError("");
        }

        // if (formikRef.current) {
        //   formikRef.current.setFieldValue("FreeText", text, false);

        //   // instantly clear/set validation
        //   formikRef.current.setFieldTouched("FreeText", true, false);

        //   // force validation refresh
        //   formikRef.current.validateField("FreeText");
        // }
      };
      editor.on("update", updateContent);
      return () => {
        editor.off("update", updateContent);
      };
    }, [editor, fontColor, fontFamily, fontSize]);

    useEffect(() => {
      if (!editor) return;
      if (!props.initialValues?.FreeTextHtml) return;

      const currentContent = editor.getHTML();
      if (
        currentContent === "<p></p>" ||
        currentContent === "" ||
        currentContent === "<p><br></p>"
      ) {
        editor.commands.setContent(props.initialValues.FreeTextHtml);

        // Apply saved styles to all text, then deselect
        editor
          .chain()
          .focus()
          .selectAll()
          .setColor(fontColor)
          .setFontFamily(fontFamily)
          .setMark("textStyle", { fontSize })
          .setTextSelection(editor.state.doc.content.size)
          .blur()
          .run();

        applyStoredMarks(editor);

        const text = editor.getText().replace(/\n/g, "");
        setFreeText(text);
        setFreeTextHTML(editor.getHTML());
        setEditorText(text);
      }
    }, [editor, props.initialValues?.FreeTextHtml]);

    const handleSubmit = (values: any) => {
      const latestText = editor?.getText().replace(/\n/g, "") || "";

      if (latestText.trim().length === 0) {
        setFreeTextError("free_text_content_required");
        return;
      }

      setFreeTextError("");

      const latestHtml = editor?.getHTML() || "";

      props.submitHandler({
        ...values,
        FreeText: latestText,
        FreeTextHTML: latestHtml,
        FontSize: Number(fontSize.replace(/\D/g, "")),
        FontFamily: FontTypeEnum[fontFamily as keyof typeof FontTypeEnum],
        TextColor: fontColor,
      });
    };
    return (
      <Formik
        initialValues={{
          ...props2.data,
          FreeText: props.initialValues?.FreeText || "",
        }}
        onSubmit={handleSubmit}
        validationSchema={AddFreeTextMediaFileValidateSchema}
        validateOnBlur={false}
        enableReinitialize
        innerRef={formikRef}
      >
        {({ values, setFieldValue, setFieldTouched }) => {
          // if (editorText !== values.FreeText) {
          //   setFieldValue("FreeText", editorText);
          // }

          return (
            <Form id="formBody">
              <div className="modal-body">
                <div className="pt-3">
                  <div className="progress px-1 step-progress">
                    <div
                      className={`progress-bar ${props.id === -1 ? "w-50" : "w-100"}`}
                      role="progressbar"
                    ></div>
                  </div>
                  <div className="step-container d-flex justify-content-between">
                    <div className="step-circle completed">
                      1{" "}
                      <span className="step-detail">
                        <FormattedMessage id="media_details" />
                      </span>
                    </div>
                    <div className="step-circle active">
                      2{" "}
                      <span className="step-detail">
                        <FormattedMessage id="free_text_config" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="form-group mb-1">
                      <Label
                        className="label-style"
                        required={true}
                        label="font"
                      />
                      <div>
                        <div className="d-flex align-items-center justify-content-start mb-2 gap-2">
                          <select
                            value={fontFamily}
                            className="form-select"
                            onChange={(e) => {
                              const newFamily = e.target.value;

                              setFontFamily(newFamily);

                              if (editor?.getText().trim().length) {
                                editor
                                  ?.chain()
                                  .focus()
                                  .selectAll()
                                  .setFontFamily(newFamily)
                                  .run();
                              }

                              ensureEditorStylePersistence(editor);
                            }}
                          >
                          <option value="Arial">{formatMessage("Arial")}</option>
                          <option value="Meiryo">{formatMessage("Meiryo")}</option>
                          <option value="MS Mincho">{formatMessage("MSMincho")}</option>
                          </select>

                          <select
                            value={fontSize}
                            className="form-select w-25"
                            onChange={(e) => {
                              const newSize = e.target.value;

                              setFontSize(newSize);

                              if (editor?.getText().trim().length) {
                                editor
                                  ?.chain()
                                  .focus()
                                  .selectAll()
                                  .setMark("textStyle", { fontSize: newSize })
                                  .run();
                              }

                              ensureEditorStylePersistence(editor);
                            }}
                          >
                            {fontSizes.map((size) => (
                              <option key={size} value={`${size}px`}>
                                {size} px
                              </option>
                            ))}
                          </select>

                          <input
                            type="color"
                            className="form-control w-25"
                            value={fontColor}
                            onChange={(e) => {
                              const newColor = e.target.value;
                              const html = editor?.getHTML() ?? "";
                              const colorMatches =
                                html.match(/color:\s*([^;'"]+)/g);
                              if (colorMatches) {
                                const uniqueColors = new Set(
                                  colorMatches.map((c) =>
                                    c.replace(/color:\s*/, "").trim(),
                                  ),
                                );
                                if (uniqueColors.size >= 1 && editor) {
                                  editor
                                    .chain()
                                    .focus()
                                    .selectAll()
                                    .setColor(newColor)
                                    .run();
                                  ensureEditorStylePersistence(editor); // ← this was missing
                                }
                              }
                              setFontColor(newColor);
                            }}
                          />

                          {fontColor}
                        </div>

                        <div className="form-group mt-1">
                          <Label
                            className="label-style"
                            required={true}
                            label="free_text_media_content"
                          />
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <EditorContent
                            editor={editor}
                            className="editor-content bg-dark"
                          />
                        </div>
                        {freeTextError && (
                          <div className="text-danger small mt-1">
                            <FormattedMessage id={freeTextError} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="height_levels"
                    />
                    <Field
                      data-testid="Category"
                      className="form-select"
                      name="Height"
                      placeholder="height_levels"
                      as={FormikFormSelect}
                      options={freeTextHeightLevelsOptions}
                    />
                  </div>
                  <div className="col-6">
                    <Label
                      className="label-style"
                      required={values.IsScroll == true ? true : false}
                      label="speed_levels"
                    />
                    <Field
                      data-testid="Category"
                      className="form-select"
                      name="SpeedTime"
                      disabled={values.IsScroll == true ? false : true}
                      placeholder="speed_levels"
                      as={FormikFormSelect}
                      options={freeTextSpeedLevelsOptions}
                    />
                  </div>
                </div>
                <div className="row align-items-center mt-3">
                  <div className="col-6">
                    <Label
                      className="label-style"
                      required={values.IsScroll == true ? true : false}
                      label="direction_type"
                    />
                    <Field
                      data-testid="Category"
                      className="form-select"
                      name="Direction"
                      disabled={values.IsScroll == true ? false : true}
                      placeholder="direction_type"
                      as={FormikFormSelect}
                      options={freeTextDirectionOptions}
                    />
                  </div>
                  <div className="col-6 mt-4">
                    <label>
                      <Field
                        data-testid="defaultSettings"
                        className="me-2"
                        name="IsScroll"
                        type="checkbox"
                        id="inlineFormCheck"
                        as={FormikFormCheckBox}
                      />
                    </label>
                    <label className="h">
                      <FormattedMessage id="is_scrollable" />
                    </label>
                  </div>
                </div>
                <div className="pb-3 mt-4 d-flex justify-content-center">
                  <Button
                    data-testid="back"
                    variant="light-secondary"
                    onClick={() => props2.prev({ ...values })}
                    disabled={props.isLoading}
                    className="mr-3"
                  >
                    <FormattedMessage id="back" />
                  </Button>

                  <LoaderButton
                    type="submit"
                    id="SaveTenant"
                    className="btn btn-primary ms-2"
                    disabled={!!freeTextError || props.isLoading}
                    isLoading={props.isLoading}
                    text="submit"
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  };

  const steps = [
    <StepOne
      next={handleNextStep}
      data={data}
      Id={props.id}
      isLoading={isLoading}
    />,
    <StepTwo
      next={handleNextStep}
      prev={handlePrevStep}
      data={data}
      Id={props.id}
      isLoading={isLoading}
    />,
  ];

  return (
    <div>
      {isLoading && <FormLoader loading={isLoading} />}
      {steps[currentStep]}
    </div>
  );
};
export default observer(AddFreeTextMediaForm);
